import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import PropTypes from 'prop-types';
import SignaturePad from 'react-signature-canvas'
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const placeholderSignature = 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xml%3Aspace%3D%22preserve%22%20width%3D%2264%22%20height%3D%2264%22%20style%3D%22fill-rule%3Aevenodd%3Bclip-rule%3Aevenodd%3Bstroke-linejoin%3Around%3Bstroke-miterlimit%3A2%22%20viewBox%3D%220%200%201280%201280%22%3E%3Cpath%20d%3D%22M0%200h1280v1280H0z%22%20style%3D%22fill%3A%23e5e9ed%22%2F%3E%3Cpath%20d%3D%22M910%20410H370v470h540V410Zm-57.333%2057.333v355.334H427.333V467.333h425.334Z%22%20style%3D%22fill%3A%23cad3db%22%2F%3E%3Cpath%20d%3D%22M810%20770H480v-60l100-170%20130%20170%20100-65v125Z%22%20style%3D%22fill%3A%23cad3db%22%2F%3E%3Ccircle%20cx%3D%22750%22%20cy%3D%22550%22%20r%3D%2250%22%20style%3D%22fill%3A%23cad3db%22%20transform%3D%22translate(10%2010)%22%2F%3E%3C%2Fsvg%3E';

// AD-2689 (Maria, 14 October 2026): Signature has a question box like the other components, for the words above the
// signature (for example the declaration the person is signing).
const Signature = ({
                       id,
                       index,
                       value,
                       onChange,
                       isEditMode,
                       isSubmitted,
                       error,
                       descriptionHtml,
                       onDescriptionHtmlChange,
                       commitDescriptionHtml,
                       isFormArchived,
                       accessLevel,
                       placeholderValues,
                       readOnly,
                       submitMode,
                       audience,
                       recipientType,
                       linkedToStudents,
                       linkedToEmployees,
                   }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml ?? '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml ?? '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftDescriptionChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange?.(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleEditorBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml?.();
    }, [commitDescriptionHtml]);
    const [sigPad, setSigPad] = useState({});
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        setSignatureDataUrl(value);
    }, [value]);

    const handleCloseDialog = () => {
        setOpenDialog(false);
        sigPad?.clear();
    };

    const handleAddSignature = () => {
        if (sigPad == null || sigPad.isEmpty()) {
            return;
        }

        const originalCanvas = sigPad.getCanvas();

        const resizedCanvas = document.createElement('canvas');
        resizedCanvas.width = 100;
        resizedCanvas.height = 100;
        const resizedCtx = resizedCanvas.getContext('2d');

        resizedCtx.fillStyle = '#fff';
        resizedCtx.fillRect(0, 0, 100, 100);
        resizedCtx.drawImage(originalCanvas, 0, 0, 100, 100);

        const signatureUrl = resizedCanvas.toDataURL('image/svg+xml');

        setSignatureDataUrl(signatureUrl);
        onChange({
            id,
            value: signatureUrl
        });
        handleCloseDialog();
    };

    const handleClearSignature = () => sigPad?.clear();

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? <>
                    {onDescriptionHtmlChange && (
                        <Box sx={{mb: 1}}>
                            <RichEditor
                                index={index}
                                isEditMode
                                isFormLive={isFormArchived}
                                accessLevel={accessLevel}
                                currentDescription={draftDescriptionHtml}
                                setCurrentDescription={handleDraftDescriptionChange}
                                audience={audience}
                                recipientType={recipientType}
                                linkedToStudents={linkedToStudents}
                                linkedToEmployees={linkedToEmployees}
                                placeholderValues={placeholderValues}
                                onFocus={() => setIsEditorFocused(true)}
                                onBlur={handleEditorBlur}
                            />
                        </Box>
                    )}
                    <img alt={'signature'} src={placeholderSignature}/>
                </>
                : <>
                    {descriptionHtml && (
                        <RichEditorResultText
                            id={id}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />
                    )}
                    <FormControl error={!!error} variant="standard">
                        <Box display={'flex'}>
                            <img alt={'signature'} src={signatureDataUrl ?? placeholderSignature}
                                 style={{cursor: isSubmitted ? '' : 'pointer'}}
                                 onClick={() => isSubmitted ? {} : setOpenDialog(true)}/>
                            {!isSubmitted &&
                                <Button onClick={() => setOpenDialog(true)}>
                                    {value ? 'Re-enter Signature' : 'Add Signature'}
                                </Button>}
                        </Box>
                        <FormHelperText>{error}</FormHelperText>
                    </FormControl>
                    <Dialog open={openDialog} onClose={handleCloseDialog}>
                        <DialogTitle>Add Signature</DialogTitle>
                        <IconButton
                            onClick={handleCloseDialog}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[600],
                            }}
                        >
                            <CloseIcon/>
                        </IconButton>
                        <DialogContent>
                            <Box>
                                <SignaturePad ref={(ref) => setSigPad(ref)} canvasProps={{width: 300, height: 300}}/>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="outlined" color="primary"
                                    onClick={() => handleClearSignature()}>Clear</Button>
                            <Button variant="contained" color="primary"
                                    onClick={() => handleAddSignature()}>Save</Button>
                        </DialogActions>
                    </Dialog>
                </>
            }
        </Box>
    );
};

Signature.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isSubmitted: PropTypes.bool,
    error: PropTypes.string,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    isFormArchived: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    readOnly: PropTypes.bool,
    submitMode: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
};

export default Signature;
